import React, { useLayoutEffect, useRef, useState } from 'react';
import { FONT_FAMILY } from '../../constants/typography';
import type { Size, ThemePalette } from '../../types';
import { toRgba } from '../../utils/color';
import { growSizeToFitLabel, sketchLabelBox, SKETCH_LABEL } from '../../utils/sketchLabel';

interface InPlaceSketchLabelProps {
  nodeId: string;
  nodeType: string;
  label: string;
  size: Size;
  position: { x: number; y: number };
  viewport: { x: number; y: number; scale: number };
  accentColor: string;
  palette: ThemePalette;
  textareaRef: React.Ref<HTMLTextAreaElement>;
  onFitSize: (nodeId: string, next: Size) => void;
  onCommit: (nodeId: string, label: string, next: Size) => void;
  onCancel: () => void;
}

export const InPlaceSketchLabel: React.FC<InPlaceSketchLabelProps> = ({
  nodeId,
  nodeType,
  label,
  size,
  position,
  viewport,
  accentColor,
  palette,
  textareaRef,
  onFitSize,
  onCommit,
  onCancel,
}) => {
  const [draft, setDraft] = useState(label);
  const cancelled = useRef(false);
  const innerRef = useRef<HTMLTextAreaElement | null>(null);
  const box = sketchLabelBox(size, nodeType);
  const sx = (position.x + box.x) * viewport.scale + viewport.x;
  const sy = (position.y + box.y) * viewport.scale + viewport.y;
  const fontPx = Math.max(11, SKETCH_LABEL.fontSize * viewport.scale);
  const linePx = SKETCH_LABEL.lineHeight * viewport.scale;

  const fit = (text: string, current: Size) =>
    growSizeToFitLabel(text, current, { type: nodeType });

  const setRefs = (el: HTMLTextAreaElement | null) => {
    innerRef.current = el;
    if (typeof textareaRef === 'function') textareaRef(el);
    else if (textareaRef) (textareaRef as React.MutableRefObject<HTMLTextAreaElement | null>).current = el;
  };

  const syncHeight = (el: HTMLTextAreaElement | null) => {
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  };

  useLayoutEffect(() => {
    syncHeight(innerRef.current);
  }, [draft, size.width, size.height, viewport.scale]);

  return (
    <div
      style={{
        position: 'fixed',
        zIndex: 9999,
        left: sx,
        top: sy,
        width: Math.max(24, box.width * viewport.scale),
        height: Math.max(24, box.height * viewport.scale),
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: toRgba(palette.canvasBg, 0.35),
        borderRadius: 4,
        boxSizing: 'border-box',
      }}
    >
      <textarea
        ref={setRefs}
        autoFocus
        rows={1}
        value={draft}
        style={{
          width: '100%',
          height: 'auto',
          background: 'transparent',
          border: 'none',
          outline: 'none',
          boxShadow: 'none',
          padding: 0,
          margin: 0,
          resize: 'none',
          overflow: 'hidden',
          textAlign: 'center',
          fontSize: fontPx,
          fontWeight: 600,
          fontFamily: FONT_FAMILY,
          color: palette.nodeText,
          caretColor: accentColor,
          lineHeight: `${linePx}px`,
          letterSpacing: 0,
          boxSizing: 'border-box',
        }}
        onChange={e => {
          const next = e.target.value;
          setDraft(next);
          syncHeight(e.currentTarget);
          const fitted = fit(next, size);
          if (fitted.width !== size.width || fitted.height !== size.height) {
            onFitSize(nodeId, fitted);
          }
        }}
        onKeyDown={e => {
          if (e.key === 'Escape') {
            cancelled.current = true;
            onCancel();
          }
          if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            e.currentTarget.blur();
          }
        }}
        onBlur={() => {
          if (cancelled.current) return;
          const val = draft.trim();
          onCommit(nodeId, val, fit(val, size));
        }}
      />
    </div>
  );
};
